/*
 * Copyright © 2017 - 2019 Atilika Inc. All rights reserved.
 */

import React, {memo} from "react";
import {createUseStyles} from "react-jss";
import LogoGrey from "../assets/images/logo-grey.svg";
import FacebookIcon from "../assets/images/icons/facebook.svg";
import GitHubIcon from "../assets/images/icons/github-blue.svg";
import LinkedInIcon from "../assets/images/icons/linked-in.svg";
import TwitterIcon from "../assets/images/icons/twitter.svg";
import {Language} from "./Language";
import Container from "./Container";
import Link from "./Link";
import UI from "./UI";

const useStyles = createUseStyles({
    root: {
        backgroundColor: UI.colors.offWhite.toCSS(),
        color: UI.colors.darkGrey.toCSS(),
        fontSize: ".875rem",
        lineHeight: 1.75,
        padding: "2rem 0"
    },
    content: {
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap"
    },
    logo: {
        marginBottom: "2rem",
        [UI.media.lte("md")]: {
            flex: "0 0 100%"
        }
    },
    navigation: {
        marginBottom: "1rem",
        [UI.media.only("xs")]: {
            flex: "0 0 50%"
        },
        "& a": {
            display: "block",
            color: UI.colors.blue.toCSS(),
            fontSize: "1rem",
            lineHeight: 1.6,
            textAlign: "left",
            textDecoration: "none",
            textTransform: "lowercase",
            "&:hover": {
                color: UI.colors.blue.toCSS(),
                textDecoration: "underline"
            }
        }
    },
    xsBreak: {
        display: "none",
        [UI.media.only("xs")]: {
            display: "inline"
        }
    },
    contact: {
        [UI.media.only("xs")]: {
            flex: "0 0 50%"
        }
    },
    social: {
        display: "flex",
        justifyContent: "space-between",
        "& a": {
            display: "block"
        },
        "& img": {
            width: 16,
            height: 16
        }
    },
    address: {
        marginBottom: "1rem"
    },
    legal: {
        marginTop: "1rem"
    }
});

interface FooterProps {
    lang: Language;
}

function tokyoAddress(lang: Language, xsBreak: string) {
    if (lang === "ja") {
        return (
            <>
                〒107-0052
                <br />
                東京都港区赤坂8-5-40
                <br />
                Pegasus Aoyama 608
            </>
        );
    }

    return (
        <>
            608 Pegasus Aoyama
            <br />
            8-5-40 Akasaka
            <br />
            Minato-ku, Tokyo <br className={xsBreak} />
            107-0052
            <br />
            Japan
        </>
    );
}

const labels = {
    en: {
        home: "Home",
        services: "Services",
        products: "Products",
        kuromoji: "Kuromoji",
        company: "Company"
    },
    ja: {
        home: "ホーム",
        services: "サービス",
        products: "製品",
        kuromoji: "Kuromoji",
        company: "企業情報"
    }
};

function Footer(props: FooterProps) {
    const classes = useStyles();
    return (
        <footer className={classes.root}>
            <Container>
                <div className={classes.content}>
                    <div className={classes.logo}>
                        <Link to={`/${props.lang}`}>
                            <img src={LogoGrey} alt="Atilika" />
                        </Link>
                    </div>
                    <nav className={classes.navigation}>
                        <Link to={`/${props.lang}/`}>{labels[props.lang].home}</Link>
                        <Link to={`/${props.lang}/services/`}>{labels[props.lang].services}</Link>
                        <Link to={`/${props.lang}/products/`}>{labels[props.lang].products}</Link>
                        <Link to={`/${props.lang}/kuromoji/`}>{labels[props.lang].kuromoji}</Link>
                        <Link to={`/${props.lang}/company/`}>{labels[props.lang].company}</Link>
                    </nav>
                    <div className={classes.contact}>
                        <div className={classes.social}>
                            <Link rel="noopener" target="_blank" href="https://twitter.com/atilika">
                                <img src={TwitterIcon} alt="Twitter" />
                            </Link>
                            <Link
                                rel="noopener"
                                target="_blank"
                                href="https://www.facebook.com/atilika/"
                            >
                                <img src={FacebookIcon} alt="Facebook" />
                            </Link>
                            <Link
                                rel="noopener"
                                target="_blank"
                                href="https://www.linkedin.com/company/atilika"
                            >
                                <img src={LinkedInIcon} alt="LinkedIn" />
                            </Link>
                            <Link
                                rel="noopener"
                                target="_blank"
                                href="https://www.github.com/atilika"
                            >
                                <img src={GitHubIcon} alt="GitHub" />
                            </Link>
                        </div>
                        <div>
                            E: <Link href="mailto:hello@atilika.com">hello@atilika.com</Link>
                            <br />
                            T: 81-3-4577-3315
                            <br />
                            F: 81-3-4577-3316
                        </div>
                    </div>
                    <div className={classes.address}>
                        {tokyoAddress(props.lang, classes.xsBreak)}
                    </div>
                    <div className={classes.address}>
                        Oslo Science Park
                        <br />
                        Gaustadalléen 21
                        <br />
                        N-0349, Oslo
                        <br />
                        Norway
                    </div>
                </div>
                <div className={classes.legal}>&copy; 2018 Atilika, Inc. All rights reserved.</div>
            </Container>
        </footer>
    );
}

export default memo(Footer);
